// Turns whatever the person drops in into something smartImport.js can read:
// either tables (arrays of arrays) or plain text. Nothing is asked of the user.
import Papa from "papaparse";
import * as XLSX from "xlsx";

export async function readAnyFile(file) {
  const ext = (file.name.split(".").pop() || "").toLowerCase();

  if (ext === "pdf") {
    const text = await extractPdfText(file);
    return { kind: "text", text, name: file.name };
  }

  if (["xlsx", "xls", "xlsm", "xlsb", "ods"].includes(ext)) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    // Every sheet is read — one-tab-per-month workbooks import in full.
    const tables = wb.SheetNames.map((name) => ({
      name,
      rows: XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: true, defval: "", blankrows: false }),
    })).filter((t) => t.rows.length >= 2);
    return { kind: "tables", tables, name: file.name };
  }

  // csv / tsv / txt / anything text-like
  const text = await file.text();
  return readPastedText(text, file.name);
}

export function readPastedText(text, name = "Pasted text") {
  const clean = String(text || "").replace(/^\uFEFF/, "");
  const lines = clean.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return { kind: "text", text: "", name };

  // Looks delimited (comma, tab, semicolon, pipe) consistently? → table.
  const delim = ["\t", ";", "|", ","].find((d) => {
    const counts = lines.slice(0, 30).map((l) => l.split(d).length - 1);
    const med = counts.slice().sort((a, b) => a - b)[Math.floor(counts.length / 2)];
    return med >= 2 && counts.filter((c) => c === med).length / counts.length > 0.6;
  });
  if (delim) {
    const parsed = Papa.parse(clean, { delimiter: delim, skipEmptyLines: true });
    return { kind: "tables", tables: [{ name: "Sheet 1", rows: parsed.data }], name };
  }
  return { kind: "text", text: clean, name };
}

// PDF → text lines, rebuilt from positioned text items. pdf.js is loaded only
// when someone actually drops a PDF, so it costs nothing otherwise.
export async function extractPdfText(file) {
  const pdfjs = await import("pdfjs-dist");
  const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const out = [];
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const content = await page.getTextContent();
    const rows = [];
    content.items.forEach((it) => {
      if (!it.str || !it.str.trim()) return;
      const y = Math.round(it.transform[5]);
      const x = it.transform[4];
      let row = rows.find((r) => Math.abs(r.y - y) <= 2);
      if (!row) { row = { y, items: [] }; rows.push(row); }
      row.items.push({ x, s: it.str });
    });
    rows.sort((a, b) => b.y - a.y);
    rows.forEach((r) => {
      r.items.sort((a, b) => a.x - b.x);
      out.push(r.items.map((i) => i.s).join("  "));
    });
  }
  return out.join("\n");
}
